import { z } from "zod";

import { SURVEY_CATEGORY_ENUM } from "../data/constants";
import type { SurveyFormValues } from "../types/survey.types";

const SQL_SERVER_DATETIME_MIN_VALUE = Date.parse("1753-01-01T00:00:00.000Z");

const questionTextSchema = z.string();
const optionalNonNegativeIntegerSchema = z.number().int().min(0).optional();
const optionalPositiveIntegerSchema = z.number().int().positive().optional();

const questionOptionSchema = z.object({
  id: z.string(),
  text: z.string().trim().min(1, "Option text is required"),
  value: z.string().trim().min(1, "Option value is required"),
  order: z.number(),
  is_other: z.boolean(),
});

const gridRowSchema = z.object({
  id: z.string(),
  text: z.string(),
  order: z.number(),
});

const gridColumnSchema = z.object({
  id: z.string(),
  text: z.string(),
  order: z.number(),
});

const createDateBoundSchema = (boundLabel: "minimum" | "maximum") =>
  z
    .string()
    .trim()
    .min(1, `Please enter a ${boundLabel} date`)
    .refine((value) => {
      const timestamp = Date.parse(value);
      return !Number.isNaN(timestamp) && timestamp > SQL_SERVER_DATETIME_MIN_VALUE;
    }, `Please enter a ${boundLabel} date after January 1, 1753`);

const createDateTimeBoundSchema = (boundLabel: "minimum" | "maximum") =>
  z
    .string()
    .trim()
    .min(1, `Please enter a ${boundLabel} datetime`)
    .refine((value) => {
      const timestamp = Date.parse(value);
      return !Number.isNaN(timestamp) && timestamp > SQL_SERVER_DATETIME_MIN_VALUE;
    }, `Please enter a ${boundLabel} datetime after January 1, 1753`);

const createQuestionSchema = <TType extends string, TData extends z.ZodObject>(
  type: TType,
  data: TData,
) =>
  z.object({
    client_id: z.string().optional(),
    is_required: z.boolean(),
    type: z.literal(type),
    position: z.number().int().positive(),
    data,
  });

const textQuestionDataSchema = z.object({
  question: questionTextSchema,
  min_length: optionalNonNegativeIntegerSchema,
  max_length: optionalNonNegativeIntegerSchema,
});

const multipleChoiceQuestionDataSchema = z.object({
  question: questionTextSchema,
  options: z.array(questionOptionSchema).min(1, "Please add at least one option"),
  allow_other: z.boolean().optional(),
});

const multipleAnswerQuestionDataSchema = multipleChoiceQuestionDataSchema.extend({
  min_select: optionalNonNegativeIntegerSchema,
  max_select: optionalNonNegativeIntegerSchema,
});

const dropdownQuestionDataSchema = multipleChoiceQuestionDataSchema.extend({
  placeholder: z.string().optional(),
});

const ratingQuestionDataSchema = z.object({
  question: questionTextSchema,
  min_rating: optionalNonNegativeIntegerSchema,
  max_rating: optionalNonNegativeIntegerSchema,
  rating_labels: z.record(z.string(), z.string()).optional(),
});

const scaleQuestionDataSchema = z.object({
  question: questionTextSchema,
  min_scale: optionalNonNegativeIntegerSchema,
  max_scale: optionalNonNegativeIntegerSchema,
  min_label: z.string().trim().min(1, "Minimum label is required"),
  max_label: z.string().trim().min(1, "Maximum label is required"),
});

const numberQuestionDataSchema = z.object({
  question: questionTextSchema,
  min_value: z.number().optional(),
  max_value: z.number().optional(),
  decimal_places: optionalNonNegativeIntegerSchema,
  unit: z.string().optional(),
});

const dateQuestionDataSchema = z.object({
  question: questionTextSchema,
  min_date: createDateBoundSchema("minimum"),
  max_date: createDateBoundSchema("maximum"),
});

const timeQuestionDataSchema = z.object({
  question: questionTextSchema,
  min_time: z.string().optional(),
  max_time: z.string().optional(),
});

const datetimeQuestionDataSchema = z
  .object({
    question: questionTextSchema,
    min_datetime: createDateTimeBoundSchema("minimum"),
    max_datetime: createDateTimeBoundSchema("maximum"),
  })
  .superRefine((data, ctx) => {
    if (Date.parse(data.min_datetime) >= Date.parse(data.max_datetime)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["max_datetime"],
        message: "Maximum datetime must be after minimum datetime",
      });
    }
  });

const fileUploadQuestionDataSchema = z.object({
  question: questionTextSchema,
  max_file_size: z.union([z.literal(5242880), z.literal(10485760), z.literal(20971520)]).optional(),
  allowed_formats: z.array(z.enum(["jpg", "jpeg", "png", "pdf"])).optional(),
  max_files: optionalPositiveIntegerSchema,
});

const emailQuestionDataSchema = z.object({
  question: questionTextSchema,
  allow_multiple: z.boolean().optional(),
});

const phoneQuestionDataSchema = z.object({
  question: questionTextSchema,
  country_code: z.string().optional(),
  format: z.literal("XXX-XXX-XXXX").optional(),
});

const yesNoQuestionDataSchema = z.object({
  question: questionTextSchema,
  yes_label: z.string().trim().min(1, "Yes label is required"),
  no_label: z.string().trim().min(1, "No label is required"),
});

const gridQuestionDataSchema = z.object({
  question: questionTextSchema,
  rows: z.array(gridRowSchema).min(1, "Please add at least one row"),
  columns: z.array(gridColumnSchema).min(1, "Please add at least one column"),
});

const questionSchemas = [
  createQuestionSchema("short_answer", textQuestionDataSchema),
  createQuestionSchema("long_answer", textQuestionDataSchema),
  createQuestionSchema("multiple_choice", multipleChoiceQuestionDataSchema),
  createQuestionSchema("multiple_answer", multipleAnswerQuestionDataSchema),
  createQuestionSchema("rating", ratingQuestionDataSchema),
  createQuestionSchema("scale", scaleQuestionDataSchema),
  createQuestionSchema("date", dateQuestionDataSchema),
  createQuestionSchema("time", timeQuestionDataSchema),
  createQuestionSchema("datetime", datetimeQuestionDataSchema),
  createQuestionSchema("file_upload", fileUploadQuestionDataSchema),
  createQuestionSchema("email", emailQuestionDataSchema),
  createQuestionSchema("phone", phoneQuestionDataSchema),
  createQuestionSchema("number", numberQuestionDataSchema),
  createQuestionSchema("yes_no", yesNoQuestionDataSchema),
  createQuestionSchema("dropdown", dropdownQuestionDataSchema),
  createQuestionSchema("multiple_choice_grid", gridQuestionDataSchema),
  createQuestionSchema("checkbox_grid", gridQuestionDataSchema),
] as const;

export const surveyQuestionSchema = z
  .discriminatedUnion("type", questionSchemas)
  .superRefine((question, ctx) => {
    if (!question.data.question.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["data", "question"],
        message: `Please enter question text for question ${question.position}`,
      });
    }
  });

export const createSurveySchema = z.object({
  title: z
    .string()
    .trim()
    .min(1, "Survey title is required")
    .min(3, "Survey title must be at least 3 characters")
    .max(120, "Survey title must be at most 120 characters"),
  description: z.string().trim().default(""),
  category: z.nativeEnum(SURVEY_CATEGORY_ENUM),
  points: z
    .number()
    .int()
    .min(0, "Points must be at least 0")
    .max(1500, "Points must be at most 1500"),
  end_time: z
    .string()
    .min(1, "Please enter end time")
    .refine((value) => !Number.isNaN(Date.parse(value)), {
      message: "Please enter a valid end time",
    })
    .refine((value) => new Date(value) > new Date(), {
      message: "End time must be in the future",
    }),
  questions: z.array(surveyQuestionSchema).min(1, "Please add at least one question"),
  is_published: z.boolean().optional(),
  publish_time: z.string().optional(),
});

interface SurveyFormSchemaOptions {
  requireFutureEndTime?: boolean;
  originalEndTime?: Date;
}

export const createSurveyFormSchema = ({
  requireFutureEndTime = true,
  originalEndTime,
}: SurveyFormSchemaOptions = {}) => {
  const surveyFormSchemaDefinition = z
    .object({
      title: createSurveySchema.shape.title,
      description: z.string().trim().max(500, "Description must be at most 500 characters"),
      category: z
        .string()
        .min(1, "Category is required")
        .refine(
          (value) => createSurveySchema.shape.category.safeParse(value).success,
          "Please select a valid category",
        ),
      points: createSurveySchema.shape.points,
      endTime: z.preprocess((value) => value, z.date({ error: "Please enter end time" })),
      organizationPopulationId: z.string().nullable(),
      questions: createSurveySchema.shape.questions,
    })
    .superRefine((formValues, ctx) => {
      const isUnchangedOriginalEndTime =
        originalEndTime && formValues.endTime.getTime() === originalEndTime.getTime();

      if (requireFutureEndTime && formValues.endTime <= new Date() && !isUnchangedOriginalEndTime) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["endTime"],
          message: "End time must be in the future",
        });
      }
    });

  return surveyFormSchemaDefinition as z.ZodType<SurveyFormValues, SurveyFormValues>;
};

export const surveyFormSchema = createSurveyFormSchema();

export type CreateSurveyFormValues = z.infer<typeof createSurveySchema>;

export const getSurveyValidationMessage = (error: z.ZodError) =>
  error.issues[0]?.message || "Please check the survey form and try again";
